/**
 * WorkerPool — manages a fixed-size pool of pre-warmed OpenSCAD web workers.
 *
 * Each pool worker is an instance of worker.ts and owns its own WASM module.
 * The pool dispatches single-pass jobs (DETECT_COLORS, GENERATE_COLOR_STL,
 * GENERATE_GHOST_STL) to whichever worker is currently idle.  If all workers
 * are busy the job is queued and dispatched as soon as a worker finishes.
 *
 * FS sync: before compiling, the caller must call syncFiles() to write project
 * files into every worker's virtual filesystem.  Pool workers share no state,
 * so every worker needs a copy of the files.
 */

import {
  BatchWriteWorkerMessageData,
  DetectColorsMessageData,
  DetectColorsResponseData,
  GenerateColorSTLMessageData,
  GenerateGhostSTLMessageData,
  GenerateSTLResponseData,
  VirtualFile,
  WorkerMessageType,
  WorkerResponseMessage,
} from './types';

/** Number of parallel workers in the pool */
export const POOL_SIZE = 4;

type PendingJob = {
  message: { type: WorkerMessageType; data: unknown };
  resolve: (data: unknown) => void;
  reject: (err: unknown) => void;
};

type PoolWorkerState = {
  worker: Worker;
  /** True while a job is running on this worker */
  busy: boolean;
  /** Pending resolve/reject for the currently running job */
  pending: {
    resolve: (data: unknown) => void;
    reject: (err: unknown) => void;
  } | null;
};

export class WorkerPool {
  private readonly workers: PoolWorkerState[];
  /** Jobs waiting for a free worker */
  private readonly queue: PendingJob[] = [];
  private terminated = false;

  /**
   * Called whenever a worker transitions between busy/idle.
   * Arguments: (activeCount, totalCount)
   */
  onBusyStateChange?: (active: number, total: number) => void;

  constructor() {
    this.workers = Array.from({ length: POOL_SIZE }, () => {
      const worker = new Worker(
        new URL('../worker/worker.ts', import.meta.url),
        { type: 'module' },
      );
      const state: PoolWorkerState = { worker, busy: false, pending: null };
      worker.addEventListener(
        'message',
        (event: MessageEvent<WorkerResponseMessage>) => {
          this.onMessage(state, event.data);
        },
      );
      worker.addEventListener('error', (event) => {
        console.error('[WorkerPool] Worker error:', event);
        state.pending?.reject(new Error(event.message ?? 'Worker error'));
        state.pending = null;
        state.busy = false;
        this.notifyBusyStateChange();
        this.drainQueue();
      });
      return state;
    });
    console.log(`[WorkerPool] Initialised with ${POOL_SIZE} workers`);
  }

  private onMessage(state: PoolWorkerState, msg: WorkerResponseMessage): void {
    // FS_WRITE_BATCH acknowledgements are handled by syncFiles() separately
    if (msg.type === WorkerMessageType.FS_WRITE_BATCH) {
      (state as any)._fsSyncResolve?.();
      (state as any)._fsSyncResolve = null;
      return;
    }

    if (!state.pending) return;
    const { resolve, reject } = state.pending;
    state.pending = null;
    state.busy = false;
    this.notifyBusyStateChange();

    if (msg.err) {
      reject(msg.err);
    } else {
      resolve(msg.data);
    }

    this.drainQueue();
  }

  private notifyBusyStateChange(): void {
    if (!this.onBusyStateChange) return;
    const active = this.workers.filter((w) => w.busy).length;
    this.onBusyStateChange(active, this.workers.length);
  }

  /** Returns the number of currently busy workers in the pool */
  getActiveCount(): number {
    return this.workers.filter((w) => w.busy).length;
  }

  /** Find an idle worker or return null */
  private idleWorker(): PoolWorkerState | null {
    return this.workers.find((w) => !w.busy) ?? null;
  }

  /** Dispatch the next queued job to an available worker, if any */
  private drainQueue(): void {
    if (this.queue.length === 0) return;
    const worker = this.idleWorker();
    if (!worker) return;
    const job = this.queue.shift()!;
    this.dispatch(worker, job);
  }

  private dispatch(state: PoolWorkerState, job: PendingJob): void {
    state.busy = true;
    state.pending = { resolve: job.resolve, reject: job.reject };
    this.notifyBusyStateChange();
    const id = Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
    state.worker.postMessage({ id, ...job.message });
  }

  /** Post a job to the pool, returns a Promise resolving with the response data */
  private post<T>(type: WorkerMessageType, data: unknown): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      if (this.terminated) {
        reject(new Error('WorkerPool has been terminated'));
        return;
      }
      const job: PendingJob = {
        message: { type, data },
        resolve: resolve as (data: unknown) => void,
        reject,
      };
      const worker = this.idleWorker();
      if (worker) {
        this.dispatch(worker, job);
      } else {
        this.queue.push(job);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Sync project files into ALL pool workers' virtual filesystems.
   * Must be called (and awaited) before running any compile jobs.
   */
  async syncFiles(files: VirtualFile[]): Promise<void> {
    if (files.length === 0) return;
    const batchData: BatchWriteWorkerMessageData = { files };
    await Promise.all(
      this.workers.map(
        (state) =>
          new Promise<void>((resolve) => {
            (state as any)._fsSyncResolve = resolve;
            const id =
              Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
            state.worker.postMessage({
              id,
              type: WorkerMessageType.FS_WRITE_BATCH,
              data: batchData,
            });
          }),
      ),
    );
  }

  /** Run color detection on a pool worker */
  detectColors(
    data: DetectColorsMessageData,
  ): Promise<DetectColorsResponseData> {
    return this.post<DetectColorsResponseData>(
      WorkerMessageType.DETECT_COLORS,
      data,
    );
  }

  /** Compile a single color pass on a pool worker */
  generateColorSTL(
    data: GenerateColorSTLMessageData,
  ): Promise<GenerateSTLResponseData> {
    return this.post<GenerateSTLResponseData>(
      WorkerMessageType.GENERATE_COLOR_STL,
      data,
    );
  }

  /** Compile the ghost geometry pass on a pool worker */
  generateGhostSTL(
    data: GenerateGhostSTLMessageData,
  ): Promise<GenerateSTLResponseData> {
    return this.post<GenerateSTLResponseData>(
      WorkerMessageType.GENERATE_GHOST_STL,
      data,
    );
  }

  /** Terminate all workers and discard any queued jobs */
  terminate(): void {
    this.terminated = true;
    for (const state of this.workers) {
      state.worker.terminate();
      state.pending?.reject(new Error('WorkerPool terminated'));
      state.pending = null;
    }
    for (const job of this.queue) {
      job.reject(new Error('WorkerPool terminated'));
    }
    this.queue.length = 0;
    console.log('[WorkerPool] Terminated');
  }
}
